(function () {
  'use strict';

  if (window.BlacklyteReveal) {
    window.BlacklyteReveal.init(document);
    return;
  }

  var GROUP_SELECTOR = '[data-bl-reveal-group]';
  var ITEM_SELECTOR = '[data-bl-reveal-item]';
  var EASING = 'cubic-bezier(0.4, 0, 0.5, 1)';
  var PRESETS = {
    section: { distance: 30, duration: 900, transformDuration: 700, stagger: 150, maxDelay: 600 },
    stagger: { distance: 30, duration: 900, transformDuration: 700, stagger: 150, maxDelay: 1350 },
    frame: { distance: 30, duration: 900, transformDuration: 700, stagger: 150, maxDelay: 300 },
  };
  var groupStates = new WeakMap();
  var knownGroups = new Set();
  var observer = null;
  var observerResizeFrame = null;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function dispatchLifecycleEvent(name, group) {
    if (typeof document.dispatchEvent !== 'function' || typeof CustomEvent !== 'function') {
      return;
    }

    document.dispatchEvent(
      new CustomEvent(name, {
        detail: { group: group },
      })
    );
  }

  function isDesignMode() {
    return Boolean(
      (window.Shopify && window.Shopify.designMode) ||
      (document.body && document.body.classList.contains('shopify-design-mode'))
    );
  }

  function canAnimate() {
    return (
      !isDesignMode() &&
      !reducedMotion.matches &&
      'IntersectionObserver' in window &&
      typeof Element !== 'undefined' &&
      typeof Element.prototype.animate === 'function'
    );
  }

  function getGroups(scope) {
    var groups = [];

    if (!scope) {
      return groups;
    }

    if (scope.nodeType === 1 && scope.matches(GROUP_SELECTOR)) {
      groups.push(scope);
    }

    if (typeof scope.querySelectorAll === 'function') {
      groups = groups.concat(Array.prototype.slice.call(scope.querySelectorAll(GROUP_SELECTOR)));
    }

    return groups;
  }

  function getItems(group) {
    return Array.prototype.slice.call(group.querySelectorAll(ITEM_SELECTOR)).filter(function (item) {
      return item.closest(GROUP_SELECTOR) === group;
    });
  }

  function readNumber(group, attribute, fallback, minimum, maximum) {
    var rawValue = group.getAttribute(attribute);
    var value;

    if (rawValue === null || rawValue.trim() === '') {
      return fallback;
    }

    value = Number(rawValue);

    if (!isFinite(value)) {
      return fallback;
    }

    return Math.min(Math.max(value, minimum), maximum);
  }

  function getConfig(group) {
    var presetName = group.getAttribute('data-bl-reveal-preset');
    var preset = PRESETS[presetName] || PRESETS.section;

    return {
      distance: readNumber(group, 'data-bl-reveal-distance', preset.distance, 0, 120),
      duration: readNumber(group, 'data-bl-reveal-duration', preset.duration, 0, 2000),
      transformDuration: readNumber(
        group,
        'data-bl-reveal-transform-duration',
        preset.transformDuration,
        0,
        2000
      ),
      stagger: readNumber(group, 'data-bl-reveal-stagger', preset.stagger, 0, 500),
      maxDelay: readNumber(group, 'data-bl-reveal-max-delay', preset.maxDelay, 0, 2000),
    };
  }

  function clearItemState(item) {
    item.removeAttribute('data-bl-reveal-state');
  }

  function cancelAnimation(animation) {
    animation.onfinish = null;
    animation.oncancel = null;

    try {
      animation.cancel();
    } catch (error) {
      // Keep cleanup moving if a non-conforming Animation object rejects cancellation.
    }
  }

  function cancelEntry(entry) {
    entry.animations.forEach(cancelAnimation);

    clearItemState(entry.item);
  }

  function stopAnimations(state) {
    if (observer) {
      observer.unobserve(state.group);
    }

    state.animations.slice().forEach(cancelEntry);

    state.animations = [];
    state.remaining = 0;
    state.group.removeAttribute('data-bl-reveal-state');
  }

  function showGroup(group) {
    var state = groupStates.get(group);

    if (!state) {
      state = { group: group, status: 'complete', animations: [], remaining: 0 };
      groupStates.set(group, state);
      knownGroups.add(group);
    } else {
      stopAnimations(state);
      state.status = 'complete';
    }

    getItems(group).forEach(clearItemState);
    dispatchLifecycleEvent('blacklyte:reveal:start', group);
  }

  function completeItem(state, entry) {
    if (state.status !== 'playing' || entry.settled) {
      return;
    }

    entry.settled = true;
    cancelEntry(entry);
    state.remaining -= 1;

    if (state.remaining === 0) {
      state.status = 'complete';
      state.animations = [];
      state.group.removeAttribute('data-bl-reveal-state');
    }
  }

  function finishAnimation(state, entry) {
    if (state.status !== 'playing' || entry.settled || entry.remainingAnimations <= 0) {
      return;
    }

    entry.remainingAnimations -= 1;

    if (entry.remainingAnimations === 0) {
      completeItem(state, entry);
    }
  }

  function createAnimations(item, config, index) {
    var computedStyle = window.getComputedStyle(item);
    var baseTransform = computedStyle.transform === 'none' ? 'translate3d(0, 0, 0)' : computedStyle.transform;
    var baseOpacity = Number(computedStyle.opacity);
    var rawOrder = item.getAttribute('data-bl-reveal-order');
    var order = rawOrder === null || rawOrder.trim() === '' ? index : Number(rawOrder);
    var delay;
    var animations = [];

    if (!isFinite(baseOpacity)) {
      baseOpacity = 1;
    }

    if (!isFinite(order) || order < 0) {
      order = index;
    }

    delay = Math.min(order * config.stagger, config.maxDelay);

    try {
      animations.push(
        item.animate([{ opacity: 0 }, { opacity: baseOpacity }], {
          duration: config.duration,
          delay: delay,
          easing: EASING,
          fill: 'both',
        })
      );
      animations.push(
        item.animate(
          [
            { transform: 'translate3d(0, ' + config.distance + 'px, 0) ' + baseTransform },
            { transform: baseTransform },
          ],
          {
            duration: config.transformDuration,
            delay: delay,
            easing: EASING,
            fill: 'both',
          }
        )
      );
      return animations;
    } catch (error) {
      animations.forEach(cancelAnimation);
      throw error;
    }
  }

  function createPreparedEntry(state, item, index) {
    var entry = { item: item, index: index, animations: [], remainingAnimations: 0, settled: false };
    var registeredIndex;

    try {
      entry.animations = createAnimations(item, state.config, index);
      entry.remainingAnimations = entry.animations.length;
      state.animations.push(entry);
      entry.animations.forEach(function (animation) {
        animation.pause();
        animation.currentTime = 0;
        animation.oncancel = function () {
          if (state.status === 'prepared') {
            showGroup(state.group);
          }
        };
      });
      item.setAttribute('data-bl-reveal-state', 'prepared');
      return entry;
    } catch (error) {
      if (entry.animations.length > 0) {
        cancelEntry(entry);
      }

      registeredIndex = state.animations.indexOf(entry);
      if (registeredIndex !== -1) {
        state.animations.splice(registeredIndex, 1);
      }

      throw error;
    }
  }

  function prepareGroup(group) {
    var items;
    var config;
    var state;

    if (groupStates.has(group)) {
      return groupStates.get(group);
    }

    if (!canAnimate() || group.getBoundingClientRect().bottom <= 0) {
      showGroup(group);
      return groupStates.get(group);
    }

    items = getItems(group);

    if (items.length === 0) {
      showGroup(group);
      return groupStates.get(group);
    }

    config = getConfig(group);
    state = { group: group, status: 'prepared', animations: [], remaining: 0, config: config };
    groupStates.set(group, state);
    knownGroups.add(group);

    try {
      items.forEach(function (item, index) {
        createPreparedEntry(state, item, index);
      });

      state.remaining = state.animations.length;
      group.setAttribute('data-bl-reveal-state', 'prepared');
      getObserver().observe(group);
    } catch (error) {
      showGroup(group);
    }

    return state;
  }

  function refreshPreparedAnimations(state) {
    var items = getItems(state.group);

    state.animations.slice().forEach(cancelEntry);
    state.animations = [];
    state.remaining = 0;

    if (items.length === 0) {
      showGroup(state.group);
      return false;
    }

    state.config = getConfig(state.group);
    items.forEach(function (item, index) {
      createPreparedEntry(state, item, index);
    });
    state.remaining = state.animations.length;
    return true;
  }

  function revealGroup(group) {
    var state = groupStates.get(group) || prepareGroup(group);

    if (!state || state.status !== 'prepared') {
      return;
    }

    if (observer) {
      observer.unobserve(group);
    }

    try {
      if (!refreshPreparedAnimations(state)) {
        return;
      }

      state.status = 'playing';
      group.setAttribute('data-bl-reveal-state', 'playing');
      dispatchLifecycleEvent('blacklyte:reveal:start', group);

      state.animations.forEach(function (entry) {
        entry.item.setAttribute('data-bl-reveal-state', 'playing');
        entry.animations.forEach(function (animation) {
          animation.onfinish = function () {
            finishAnimation(state, entry);
          };
          animation.oncancel = function () {
            completeItem(state, entry);
          };
          animation.play();
        });
      });
    } catch (error) {
      showGroup(group);
    }
  }

  function getObserverRootMargin() {
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    return '0px 0px -' + Math.round(viewportHeight * 0.2) + 'px 0px';
  }

  function getObserver() {
    if (!observer) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
              revealGroup(entry.target);
            }
          });
        },
        { root: null, rootMargin: getObserverRootMargin(), threshold: 0.01 }
      );
    }

    return observer;
  }

  function refreshObserver() {
    if (!observer) {
      return;
    }

    observer.disconnect();
    observer = null;

    Array.from(knownGroups).forEach(function (group) {
      var state = groupStates.get(group);

      if (state && state.status === 'prepared') {
        getObserver().observe(group);
      }
    });
  }

  function handleViewportResize() {
    if (observerResizeFrame !== null) {
      return;
    }

    observerResizeFrame = window.requestAnimationFrame(function () {
      observerResizeFrame = null;
      refreshObserver();
    });
  }

  function init(scope) {
    getGroups(scope || document).forEach(prepareGroup);
  }

  function reveal(scope) {
    getGroups(scope || document).forEach(showGroup);
  }

  function destroy(scope) {
    getGroups(scope || document).forEach(function (group) {
      var state = groupStates.get(group);

      if (state) {
        stopAnimations(state);
        groupStates.delete(group);
        knownGroups.delete(group);
      }
    });
  }

  function handleBlockSelect(event) {
    var target = event.target;
    var group = target && target.nodeType === 1 ? target.closest(GROUP_SELECTOR) : null;

    if (!group && target && typeof target.querySelector === 'function') {
      group = target.querySelector(GROUP_SELECTOR);
    }

    if (group) {
      showGroup(group);
    }
  }

  function handleMotionChange() {
    if (reducedMotion.matches) {
      Array.from(knownGroups).forEach(showGroup);
      reveal(document);
    } else {
      init(document);
    }
  }

  window.BlacklyteReveal = {
    init: init,
    reveal: reveal,
    destroy: destroy,
  };

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });
  document.addEventListener('shopify:block:select', handleBlockSelect);
  window.addEventListener('resize', handleViewportResize);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      reveal(document);
    }
  });

  if (typeof reducedMotion.addEventListener === 'function') {
    reducedMotion.addEventListener('change', handleMotionChange);
  } else {
    reducedMotion.addListener(handleMotionChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
